import { memo } from 'react';
import { NumericFormat } from 'react-number-format';
import Form from 'react-bootstrap/Form';
import Button from 'react-bootstrap/Button';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import moment from 'moment';

function AggregatorBillingKPIs({
  aggregatorMonthlySubscriptionFee,
  aggregatorNumberOfEmployees,
  aggregatorNumberOfAddressableEmployees,
  aggregatorTurnoverBenchmerk,
  aggregatorTurnoverBenchmerkSource,
  aggregatorEmployeeReplacementCost,
  aggregatorEmployeeReplacementCostSource,
  aggregatorTurnoverCalculation,
  aggregatorTurnoverCalcForNonBillableStatus,
  changeValue,
  changeChecked,
}) {
  return (
    <div className="overflow-auto">
      <div className="mx-3 mt-4 d-flex align-items-center">
        <Form.Label className="bo-min-w-280 mb-0">Monthly Subscription Fee ($):</Form.Label>
        <NumericFormat
          className="bo-input bo-w-100"
          id="monthlySubscriptionFee"
          name="monthlySubscriptionFee"
          value={aggregatorMonthlySubscriptionFee || ''}
          customInput={Form.Control}
          onChange={changeValue}
          allowNegative={false}
          decimalScale={2}
        />
      </div>
      <div className="mx-3 mt-2 d-flex align-items-center">
        <Form.Label className="bo-min-w-280 mb-0">Number of Employees:</Form.Label>
        <NumericFormat
          className="bo-input bo-w-100"
          id="numberOfEmployees"
          name="numberOfEmployees"
          value={aggregatorNumberOfEmployees || ''}
          customInput={Form.Control}
          onChange={changeValue}
          allowNegative={false}
          decimalScale={0}
        />
      </div>
      <div className="mx-3 mt-2 d-flex align-items-center">
        <Form.Label className="bo-min-w-280 mb-0">Number of Addressable Employees:</Form.Label>
      </div>
      {Object.entries(aggregatorNumberOfAddressableEmployees).map(([key, value]) => (
        <div key={key} className="mx-5 my-2 d-flex align-items-center">
          <Form.Label className="me-3 mb-0">From Date:</Form.Label>
          <DatePicker
            className="bo-input bo-date-picker"
            onChange={date => {
              const keys = Object.keys(aggregatorNumberOfAddressableEmployees);
              const newObj = keys.reduce((acc, val) => {
                if (val === key) {
                  acc[moment(date).format('YYYY-MM-DD')] =
                    aggregatorNumberOfAddressableEmployees[key];
                } else {
                  acc[val] = aggregatorNumberOfAddressableEmployees[val];
                }

                return acc;
              }, {});

              changeValue({
                target: {
                  name: 'numberOfAddressableEmployees',
                  value: newObj,
                },
              });
            }}
            selected={new Date(key)}
            customInput={<Form.Control />}
            excludeDates={Object.keys(aggregatorNumberOfAddressableEmployees).map(x => new Date(x))}
            dateFormat="yyyy-MM-dd"
          />
          <Form.Label className="mx-3 bo-w-80 mb-0">Employees:</Form.Label>
          <NumericFormat
            className="bo-input bo-w-100"
            value={value}
            customInput={Form.Control}
            onChange={e =>
              changeValue({
                target: {
                  name: 'numberOfAddressableEmployees',
                  value: {
                    ...aggregatorNumberOfAddressableEmployees,
                    [key]: Number(e.target.value),
                  },
                },
              })
            }
            min={0}
            allowNegative={false}
            decimalScale={0}
          />
          <Button
            className="bo-link p-0 mx-3"
            onClick={() =>
              changeValue({
                target: {
                  name: 'numberOfAddressableEmployees',
                  value: Object.fromEntries(
                    Object.entries(aggregatorNumberOfAddressableEmployees).filter(
                      ([k]) => k !== key,
                    ),
                  ),
                },
              })
            }
          >
            Delete
          </Button>
        </div>
      ))}
      <Button
        className="bo-link p-0 mx-5"
        onClick={() => {
          const keys = Object.keys(aggregatorNumberOfAddressableEmployees);

          if (keys.length > 0) {
            const lastDate = new Date(
              Math.max(
                ...Object.keys(aggregatorNumberOfAddressableEmployees).map(k => new Date(k)),
              ),
            );

            changeValue({
              target: {
                name: 'numberOfAddressableEmployees',
                value: {
                  ...aggregatorNumberOfAddressableEmployees,
                  [moment(lastDate).add(1, 'day').format('YYYY-MM-DD')]: 0,
                },
              },
            });
          } else {
            changeValue({
              target: {
                name: 'numberOfAddressableEmployees',
                value: {
                  ...aggregatorNumberOfAddressableEmployees,
                  [moment().format('YYYY-MM-DD')]: 0,
                },
              },
            });
          }
        }}
      >
        Add
      </Button>
      <div className="mx-3 mt-2 d-flex align-items-center">
        <div className="bo-w-32 d-flex justify-content-center align-items-top">
          <Form.Check
            name="turnoverCalculation"
            id="turnoverCalculation"
            checked={aggregatorTurnoverCalculation || false}
            onChange={changeChecked}
          />
        </div>
        <Form.Label className="p-0 m-0">Calculate Turnover</Form.Label>
      </div>
      <div className="mx-5 mt-2 d-flex align-items-center">
        <Form.Label
          className={`bo-min-w-280 mb-0 ${
            !aggregatorTurnoverCalculation ? 'bo-text-light-gray' : ''
          }`}
        >
          Annual Turnover Benchmark:
        </Form.Label>
        <NumericFormat
          className="bo-input bo-w-100"
          id="turnoverBenchmerk"
          name="turnoverBenchmerk"
          value={aggregatorTurnoverBenchmerk || ''}
          customInput={Form.Control}
          onChange={changeValue}
          allowNegative={false}
          decimalScale={2}
          disabled={!aggregatorTurnoverCalculation}
        />
        <Form.Label
          className={`mx-3 bo-w-60 mb-0 ${
            !aggregatorTurnoverCalculation ? 'bo-text-light-gray' : ''
          }`}
        >
          Source:
        </Form.Label>
        <div>
          <Form.Control
            className="bo-input"
            type="text"
            name="turnoverBenchmerkSource"
            id="turnoverBenchmerkSource"
            value={aggregatorTurnoverBenchmerkSource || ''}
            onChange={changeValue}
            disabled={!aggregatorTurnoverCalculation}
          />
        </div>
      </div>
      <div className="mx-5 my-2 d-flex align-items-center">
        <Form.Label
          className={`bo-min-w-280 mb-0 ${
            !aggregatorTurnoverCalculation ? 'bo-text-light-gray' : ''
          }`}
        >
          Employee Replacement Cost ($):
        </Form.Label>
        <div>
          <NumericFormat
            className="bo-input bo-w-100"
            id="employeeReplacementCost"
            name="employeeReplacementCost"
            value={aggregatorEmployeeReplacementCost || ''}
            customInput={Form.Control}
            onChange={changeValue}
            allowNegative={false}
            decimalScale={2}
            disabled={!aggregatorTurnoverCalculation}
          />
        </div>
        <Form.Label
          className={`mx-3 bo-w-60 mb-0 ${
            !aggregatorTurnoverCalculation ? 'bo-text-light-gray' : ''
          }`}
        >
          Source:
        </Form.Label>
        <div>
          <Form.Control
            className="bo-input"
            type="text"
            name="employeeReplacementCostSource"
            id="employeeReplacementCostSource"
            value={aggregatorEmployeeReplacementCostSource || ''}
            onChange={changeValue}
            disabled={!aggregatorTurnoverCalculation}
          />
        </div>
      </div>
      <div className="mx-5 my-2 d-flex align-items-center">
        <div className="bo-w-32 d-flex justify-content-center align-items-top">
          <Form.Check
            name="turnoverCalcForNonBillableStatus"
            id="turnoverCalcForNonBillableStatus"
            checked={aggregatorTurnoverCalcForNonBillableStatus || false}
            onChange={changeChecked}
            disabled={!aggregatorTurnoverCalculation}
          />
        </div>
        <Form.Label
          className={`p-0 m-0 ${!aggregatorTurnoverCalculation ? 'bo-text-light-gray' : ''}`}
        >
          Calculate for non-billable users
        </Form.Label>
      </div>
    </div>
  );
}

export default memo(AggregatorBillingKPIs);
